import { UseFormReturn } from "react-hook-form";
import { FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { PrenatalIntakeFormData } from "../PrenatalIntakeFormSchema";

interface CurrentPregnancySectionProps {
  form: UseFormReturn<PrenatalIntakeFormData>;
}

export function CurrentPregnancySection({ form }: CurrentPregnancySectionProps) {
  return (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold border-b pb-2">Section II: Current Pregnancy</h3>

      {/* Multiple Pregnancy */}
      <FormField
        control={form.control}
        name="is_multiple_pregnancy"
        render={({ field }) => (
          <FormItem className="flex items-start space-x-3 space-y-0">
            <FormControl>
              <Checkbox
                checked={field.value}
                onCheckedChange={field.onChange}
              />
            </FormControl>
            <div className="space-y-1 leading-none">
              <FormLabel>Pregnant with more than one baby (twins, triplets, etc.)</FormLabel>
              <FormDescription>This is a risk factor for PNCC eligibility</FormDescription>
            </div>
          </FormItem>
        )}
      />

      {/* Due Date & First Appointment */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FormField
          control={form.control}
          name="due_date"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Expected Due Date</FormLabel>
              <FormControl>
                <Input type="date" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="first_prenatal_appointment"
          render={({ field }) => (
            <FormItem>
              <FormLabel>First Prenatal Appointment Date</FormLabel>
              <FormControl>
                <Input type="date" {...field} />
              </FormControl>
              <FormDescription>First medical appointment related to this pregnancy</FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      {/* WIC */}
      <FormField
        control={form.control}
        name="receiving_wic"
        render={({ field }) => (
          <FormItem className="flex items-start space-x-3 space-y-0">
            <FormControl>
              <Checkbox
                checked={field.value}
                onCheckedChange={field.onChange}
              />
            </FormControl>
            <div className="space-y-1 leading-none">
              <FormLabel>Currently receiving WIC services</FormLabel>
              <FormDescription>Special Supplemental Nutrition Program for Women, Infants, and Children</FormDescription>
            </div>
          </FormItem>
        )}
      />

      {/* Weight & Height */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <FormField
          control={form.control}
          name="weight_before_pregnancy"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Weight Before Pregnancy (lbs)</FormLabel>
              <FormControl>
                <Input type="number" placeholder="e.g., 150" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="current_weight"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Current Weight (lbs)</FormLabel>
              <FormControl>
                <Input type="number" placeholder="e.g., 165" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="height"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Height</FormLabel>
              <FormControl>
                <Input placeholder="e.g., 5 feet 6 inches" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      {/* Pregnancy Notes */}
      <FormField
        control={form.control}
        name="pregnancy_going_well_notes"
        render={({ field }) => (
          <FormItem>
            <FormLabel>What is going well in the pregnancy so far?</FormLabel>
            <FormDescription>Medically, emotionally, or socially</FormDescription>
            <FormControl>
              <Textarea 
                placeholder="Describe what's going well..." 
                className="min-h-[80px]"
                {...field} 
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="pregnancy_goals"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Goals for this pregnancy</FormLabel>
            <FormDescription>Nutritional goals, habit goals, or emotional goals</FormDescription>
            <FormControl>
              <Textarea 
                placeholder="What are your goals for this pregnancy?" 
                className="min-h-[80px]"
                {...field} 
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      {/* Pregnancy Timing Preference */}
      <FormField
        control={form.control}
        name="pregnancy_timing_preference"
        render={({ field }) => (
          <FormItem>
            <FormLabel>If you could change the timing of this pregnancy, would it be...</FormLabel>
            <FormControl>
              <RadioGroup
                onValueChange={field.onChange}
                defaultValue={field.value}
                className="flex flex-col space-y-2"
              >
                <FormItem className="flex items-center space-x-3 space-y-0">
                  <FormControl>
                    <RadioGroupItem value="earlier" />
                  </FormControl>
                  <FormLabel className="font-normal">Earlier</FormLabel>
                </FormItem>
                <FormItem className="flex items-center space-x-3 space-y-0">
                  <FormControl>
                    <RadioGroupItem value="later" />
                  </FormControl>
                  <FormLabel className="font-normal">Later</FormLabel>
                </FormItem>
                <FormItem className="flex items-center space-x-3 space-y-0">
                  <FormControl>
                    <RadioGroupItem value="no_change" />
                  </FormControl>
                  <FormLabel className="font-normal">No change</FormLabel>
                </FormItem>
                <FormItem className="flex items-center space-x-3 space-y-0">
                  <FormControl>
                    <RadioGroupItem value="no_pregnancy" />
                  </FormControl>
                  <FormLabel className="font-normal">Would prefer not to be pregnant</FormLabel>
                </FormItem>
              </RadioGroup>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      {/* Breastfeeding */}
      <FormField
        control={form.control}
        name="planning_to_breastfeed"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Planning to breastfeed?</FormLabel>
            <Select onValueChange={field.onChange} value={field.value}>
              <FormControl>
                <SelectTrigger>
                  <SelectValue placeholder="Select option" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                <SelectItem value="yes">Yes</SelectItem>
                <SelectItem value="no">No</SelectItem>
                <SelectItem value="undecided">Undecided</SelectItem>
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="breastfeeding_knowledge"
        render={({ field }) => (
          <FormItem>
            <FormLabel>What do you know about breastfeeding?</FormLabel>
            <FormDescription>Thoughts or experiences with breastfeeding</FormDescription>
            <FormControl>
              <Textarea 
                placeholder="Share your knowledge or experience..." 
                className="min-h-[80px]"
                {...field} 
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      {/* Bleeding/Cramping */}
      <div className="space-y-4">
        <FormField
          control={form.control}
          name="bleeding_or_cramping"
          render={({ field }) => (
            <FormItem className="flex items-start space-x-3 space-y-0">
              <FormControl>
                <Checkbox
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
              <div className="space-y-1 leading-none">
                <FormLabel>Any bleeding or cramping during this pregnancy?</FormLabel>
              </div>
            </FormItem>
          )}
        />

        {form.watch("bleeding_or_cramping") && (
          <FormField
            control={form.control}
            name="bleeding_cramping_notes"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Please describe</FormLabel>
                <FormControl>
                  <Textarea 
                    placeholder="Describe the bleeding or cramping..." 
                    className="min-h-[60px]"
                    {...field} 
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}
      </div>
    </div>
  );
}
